/**
 * Pivot Produção (volumetria): Mês expande colunas MM/yyyy; Prod. e Med. Atual sob cada mês.
 */

import { FONTE_PRODUCAO_VOLUMETRIA } from '../constants/relatoriosFontes';
import {
  COLUMNS_CONFIG_PRODUCAO,
  PRODUCAO_COUNTER_COLUMN_IDS,
  PRODUCAO_MED_COLUMN_IDS,
  PRODUCAO_MES_COLUMN_ID,
  PRODUCAO_VOLUMETRIA_METRIC_COLUMN_IDS,
} from '../constants/relatoriosColumnsProducaoVolumetria';
export { PRODUCAO_COUNTER_COLUMN_IDS, PRODUCAO_MED_COLUMN_IDS, PRODUCAO_MES_COLUMN_ID };

export const PRODUCAO_MES_COLUMN_KEY = `${FONTE_PRODUCAO_VOLUMETRIA}:${PRODUCAO_MES_COLUMN_ID}`;
export const PRODUCAO_MES_GROUP_PREFIX = 'producao_mes_group:';

export const PROD_DISPLAY_MES = 'mes';
export const PROD_DISPLAY_TOTAL = 'total';
export const PROD_DISPLAY_MEDIA = 'media';

export function normalizeProducaoDisplayMode(value) {
  if (value === PROD_DISPLAY_TOTAL) return PROD_DISPLAY_TOTAL;
  if (value === PROD_DISPLAY_MEDIA) return PROD_DISPLAY_MEDIA;
  return PROD_DISPLAY_MES;
}

export function getProducaoMesRef(columnRefs) {
  return (columnRefs || []).find(
    (r) => r.fonte === FONTE_PRODUCAO_VOLUMETRIA && r.id === PRODUCAO_MES_COLUMN_ID
  );
}

export function getProducaoDisplayMode(columnRefs) {
  return normalizeProducaoDisplayMode(getProducaoMesRef(columnRefs)?.prodDisplayMode);
}

export function isProducaoTotalMode(columnRefs) {
  return getProducaoDisplayMode(columnRefs) === PROD_DISPLAY_TOTAL;
}

export function isProducaoMediaMode(columnRefs) {
  return getProducaoDisplayMode(columnRefs) === PROD_DISPLAY_MEDIA;
}

/** Total ou Média: uma coluna por métrica (sem pivot mensal). */
export function isProducaoAggregatedMode(columnRefs) {
  const mode = getProducaoDisplayMode(columnRefs);
  return mode === PROD_DISPLAY_TOTAL || mode === PROD_DISPLAY_MEDIA;
}

export function producaoTotalColumnKey(metricId) {
  return `${FONTE_PRODUCAO_VOLUMETRIA}:${metricId}`;
}

export function producaoMesGroupColumnId(monthSlug) {
  return `${PRODUCAO_MES_GROUP_PREFIX}${monthSlug}`;
}

export function isProducaoCounterColumnId(id) {
  return PRODUCAO_COUNTER_COLUMN_IDS.includes(id);
}

export function isProducaoMedColumnId(id) {
  return PRODUCAO_MED_COLUMN_IDS.includes(id);
}

export function isProducaoVolumetriaMetricColumnId(id) {
  return PRODUCAO_VOLUMETRIA_METRIC_COLUMN_IDS.includes(id);
}

/** Agrupa colunas por mês no cabeçalho quando há 2+ métricas (Prod. ou Med.). */
export function shouldGroupProducaoByMonth(columnRefs) {
  return getProducaoVolumetriaRefs(columnRefs).length > 1;
}

export function countDistinctProducaoCountersInKeys(keys) {
  const ids = new Set();
  for (const key of keys || []) {
    const p = parseProducaoPivotKey(key);
    if (p) ids.add(p.prodId);
  }
  return ids.size;
}

export function mesToSlug(dateVal) {
  const d = dateVal instanceof Date ? dateVal : new Date(dateVal);
  if (Number.isNaN(d.getTime())) return '_';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

export function mesLabelFromSlug(slug) {
  if (!slug || slug === '_') return '';
  const m = /^(\d{4})-(\d{2})$/.exec(String(slug).trim());
  if (!m) return String(slug);
  return `${m[2]}/${m[1]}`;
}

function dateToYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Período padrão ao adicionar Mês: hoje + últimos 30 dias (31 dias corridos). */
export function getDefaultProducaoMesPeriodo() {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 30);
  return { startDate: dateToYmd(start), endDate: dateToYmd(end) };
}

export function producaoPivotColumnKey(metricId, monthSlug) {
  return `${FONTE_PRODUCAO_VOLUMETRIA}:${metricId}:${monthSlug}`;
}

export function isProducaoCounterKey(key) {
  const p = parseColumnRefKeySimple(key);
  return p?.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoCounterColumnId(p.id);
}

export function isProducaoVolumetriaMetricKey(key) {
  const p = parseColumnRefKeySimple(key);
  return p?.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoVolumetriaMetricColumnId(p.id);
}

function parseColumnRefKeySimple(key) {
  if (!key || typeof key !== 'string') return null;
  const i = key.indexOf(':');
  if (i <= 0) return null;
  const rest = key.slice(i + 1);
  const j = rest.indexOf(':');
  if (j > 0) return { fonte: key.slice(0, i), id: rest.slice(0, j), monthSlug: rest.slice(j + 1) };
  return { fonte: key.slice(0, i), id: rest };
}

export function parseProducaoPivotKey(key) {
  const p = parseColumnRefKeySimple(key);
  if (p?.fonte !== FONTE_PRODUCAO_VOLUMETRIA || !p.monthSlug) return null;
  if (!isProducaoVolumetriaMetricColumnId(p.id)) return null;
  return {
    prodId: p.id,
    monthSlug: p.monthSlug,
    label: mesLabelFromSlug(p.monthSlug),
  };
}

export function getProducaoProdRefs(columnRefs) {
  return (columnRefs || []).filter(
    (r) => r.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoCounterColumnId(r.id)
  );
}

export function getProducaoMedRefs(columnRefs) {
  return (columnRefs || []).filter(
    (r) => r.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoMedColumnId(r.id)
  );
}

export function getProducaoVolumetriaRefs(columnRefs) {
  return (columnRefs || []).filter(
    (r) => r.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoVolumetriaMetricColumnId(r.id)
  );
}

export function hasProducaoMesRef(columnRefs) {
  return Boolean(getProducaoMesRef(columnRefs));
}

/** Mês sem Prod./Med. — dados de volumetria não aparecem na tabela. */
export function needsProducaoMetricsForMes(columnRefs) {
  if (!hasProducaoMesRef(columnRefs)) return false;
  return getProducaoVolumetriaRefs(columnRefs).length === 0;
}

function sortMeses(meses) {
  return [...(meses || [])].sort((a, b) => String(a.slug).localeCompare(String(b.slug)));
}

/** Meses entre start/end (YYYY-MM-DD), inclusive, para cabeçalhos quando a API ainda não retornou meta. */
export function buildMesesRangeFromPeriod(startDate, endDate) {
  if (!startDate || !endDate || startDate > endDate) return [];
  const [sy, sm] = String(startDate).slice(0, 10).split('-').map(Number);
  const [ey, em] = String(endDate).slice(0, 10).split('-').map(Number);
  if (!sy || !sm || !ey || !em) return [];
  const out = [];
  let y = sy;
  let m = sm;
  while (y < ey || (y === ey && m <= em)) {
    const slug = `${y}-${String(m).padStart(2, '0')}`;
    out.push({ slug, label: mesLabelFromSlug(slug) });
    m += 1;
    if (m > 12) {
      m = 1;
      y += 1;
    }
  }
  return out;
}

export function resolveProducaoMeses(meta, columnRefs, appliedPeriod, orderedKeys = []) {
  const mesRef = (columnRefs || []).find(
    (r) => r.fonte === FONTE_PRODUCAO_VOLUMETRIA && r.id === PRODUCAO_MES_COLUMN_ID
  );
  const fromApi = sortMeses(meta?.mesesDisponiveis);
  const p = mesRef?.mesPeriodo;
  const fromMesPeriodo =
    p?.startDate && p?.endDate ? buildMesesRangeFromPeriod(p.startDate, p.endDate) : [];

  // Período configurado na coluna Mês tem prioridade sobre meta da API (evita sumir colunas antes/durante enrich).
  if (fromMesPeriodo.length > 0) {
    return mergeMesesWithPivotSlugs(fromMesPeriodo, orderedKeys);
  }
  if (fromApi.length > 0) {
    return mergeMesesWithPivotSlugs(fromApi, orderedKeys);
  }
  if (appliedPeriod?.startDate && appliedPeriod?.endDate) {
    return mergeMesesWithPivotSlugs(
      buildMesesRangeFromPeriod(appliedPeriod.startDate, appliedPeriod.endDate),
      orderedKeys
    );
  }
  if (mesRef) {
    const def = getDefaultProducaoMesPeriodo();
    return mergeMesesWithPivotSlugs(
      buildMesesRangeFromPeriod(def.startDate, def.endDate),
      orderedKeys
    );
  }
  return mergeMesesWithPivotSlugs([], orderedKeys);
}

function mergeMesesWithPivotSlugs(meses, keys) {
  const slugMap = new Map();
  for (const m of meses || []) {
    if (m?.slug) slugMap.set(m.slug, m.label ? m : { slug: m.slug, label: mesLabelFromSlug(m.slug) });
  }
  for (const key of keys || []) {
    const p = parseProducaoPivotKey(key);
    if (p?.monthSlug && !slugMap.has(p.monthSlug)) {
      slugMap.set(p.monthSlug, { slug: p.monthSlug, label: mesLabelFromSlug(p.monthSlug) });
    }
  }
  return [...slugMap.values()].sort((a, b) => String(a.slug).localeCompare(String(b.slug)));
}

export function buildOrderedProducaoPivotKeys(meses, metricRefs) {
  const keys = [];
  for (const m of meses || []) {
    for (const p of metricRefs || []) {
      keys.push(producaoPivotColumnKey(p.id, m.slug));
    }
  }
  return keys;
}

export function isProducaoPivotSlotKey(key) {
  return (
    key === PRODUCAO_MES_COLUMN_KEY ||
    isProducaoVolumetriaMetricKey(key) ||
    Boolean(parseProducaoPivotKey(key))
  );
}

/** Separa chaves da tabela: parque/chamados vs produção (pivot ou total). */
export function partitionDisplayKeysForProducao(keys, columnRefs) {
  const list = keys || [];
  const isAggregated = isProducaoAggregatedMode(columnRefs);
  const producaoPivotKeys = isAggregated ? [] : list.filter((k) => parseProducaoPivotKey(k));
  const producaoTotalKeys = isAggregated ? list.filter((k) => isProducaoVolumetriaMetricKey(k)) : [];
  const producaoKeys = isAggregated ? producaoTotalKeys : producaoPivotKeys;
  const insertIndex = list.findIndex((k) => isProducaoPivotSlotKey(k));
  const baseKeys = list.filter((k) => !isProducaoPivotSlotKey(k));
  return {
    baseKeys,
    producaoPivotKeys: producaoKeys,
    producaoTotalKeys,
    isTotal: isAggregated,
    isAggregated,
    insertIndex: insertIndex >= 0 ? insertIndex : list.length,
  };
}

function parseProducaoInt(val) {
  if (val == null || val === '' || val === '-') return null;
  const digits = String(val).replace(/\D/g, '');
  if (!digits) return null;
  const n = Number(digits);
  return Number.isNaN(n) ? null : n;
}

export function getProducaoMediaMonthCount(meta, columnRefs, appliedPeriod) {
  return resolveProducaoMeses(meta, columnRefs, appliedPeriod).length;
}

export function getProducaoRowCellValue(row, columnKey, ctx = null) {
  if (!row || !columnKey) return '-';
  const v = row[columnKey];
  const raw = v != null && v !== '' ? v : '-';
  const p = parseColumnRefKeySimple(columnKey);
  if (p?.fonte === FONTE_PRODUCAO_VOLUMETRIA && isProducaoMedColumnId(p.id)) {
    return raw;
  }
  if (!ctx?.columnRefs || !isProducaoMediaMode(ctx.columnRefs)) return raw;
  if (p?.fonte !== FONTE_PRODUCAO_VOLUMETRIA || !isProducaoCounterColumnId(p.id)) return raw;
  const total = parseProducaoInt(raw);
  const n = getProducaoMediaMonthCount(ctx.meta, ctx.columnRefs, ctx.appliedPeriod);
  if (total == null || n <= 0) return raw;
  return String(Math.round(total / n));
}

export function expandProducaoDisplayColumnKeys(orderedKeys, meta, columnRefs, appliedPeriod) {
  const keys = [...(orderedKeys || [])];
  const hasMes = hasProducaoMesRef(columnRefs) || keys.includes(PRODUCAO_MES_COLUMN_KEY);
  if (!hasMes) {
    return keys.filter((k) => !isProducaoVolumetriaMetricKey(k) && !parseProducaoPivotKey(k));
  }

  const metricRefs = getProducaoVolumetriaRefs(columnRefs);
  if (metricRefs.length === 0) {
    return keys.filter((k) => !isProducaoPivotSlotKey(k));
  }

  if (isProducaoAggregatedMode(columnRefs)) {
    const totalBlock = metricRefs.map((p) => producaoTotalColumnKey(p.id));
    const out = [];
    let inserted = false;
    for (const key of keys) {
      if (isProducaoPivotSlotKey(key)) {
        if (!inserted) {
          out.push(...totalBlock);
          inserted = true;
        }
        continue;
      }
      out.push(key);
    }
    if (!inserted) out.push(...totalBlock);
    return out;
  }

  let meses = resolveProducaoMeses(meta, columnRefs, appliedPeriod, keys);
  if (meses.length === 0) {
    const def = getDefaultProducaoMesPeriodo();
    meses = buildMesesRangeFromPeriod(def.startDate, def.endDate);
  }
  if (meses.length === 0) {
    return keys.filter((k) => !isProducaoPivotSlotKey(k));
  }

  const pivotBlock = buildOrderedProducaoPivotKeys(meses, metricRefs);
  const out = [];
  let pivotInserted = false;
  for (const key of keys) {
    if (isProducaoPivotSlotKey(key)) {
      if (!pivotInserted) {
        out.push(...pivotBlock);
        pivotInserted = true;
      }
      continue;
    }
    out.push(key);
  }
  if (!pivotInserted) out.push(...pivotBlock);
  return out;
}

export function collapseProducaoDisplayKeys(keys) {
  const out = [];
  const seenMes = new Set();
  const seenMetric = new Set();
  for (const key of keys || []) {
    const pivot = parseProducaoPivotKey(key);
    if (pivot) {
      if (!seenMes.has(PRODUCAO_MES_COLUMN_KEY)) {
        out.push(PRODUCAO_MES_COLUMN_KEY);
        seenMes.add(PRODUCAO_MES_COLUMN_KEY);
      }
      const metricKey = `${FONTE_PRODUCAO_VOLUMETRIA}:${pivot.prodId}`;
      if (!seenMetric.has(metricKey)) {
        out.push(metricKey);
        seenMetric.add(metricKey);
      }
      continue;
    }
    if (isProducaoVolumetriaMetricKey(key)) {
      if (!seenMes.has(PRODUCAO_MES_COLUMN_KEY)) {
        out.push(PRODUCAO_MES_COLUMN_KEY);
        seenMes.add(PRODUCAO_MES_COLUMN_KEY);
      }
      if (!seenMetric.has(key)) {
        out.push(key);
        seenMetric.add(key);
      }
      continue;
    }
    out.push(key);
  }
  return out;
}

export function buildProducaoPivotColumnLabels(meta, columnRefs, appliedPeriod) {
  const labels = {};
  const metricRefs = getProducaoVolumetriaRefs(columnRefs);
  const metricLabels = Object.fromEntries(
    COLUMNS_CONFIG_PRODUCAO.map((c) => [c.id, c.label])
  );
  if (isProducaoAggregatedMode(columnRefs)) {
    for (const p of metricRefs) {
      const colKey = producaoTotalColumnKey(p.id);
      const base = metricLabels[p.id] || p.id;
      if (isProducaoMedColumnId(p.id)) {
        labels[colKey] = base;
      } else {
        labels[colKey] = isProducaoMediaMode(columnRefs) ? `${base} (m\u00e9dia)` : base;
      }
    }
    return labels;
  }
  let meses = resolveProducaoMeses(meta, columnRefs, appliedPeriod, []);
  if (meses.length === 0) {
    const def = getDefaultProducaoMesPeriodo();
    meses = buildMesesRangeFromPeriod(def.startDate, def.endDate);
  }
  const groupByMonth = metricRefs.length > 1;
  for (const m of meses) {
    const mesLabel = m.label || mesLabelFromSlug(m.slug);
    if (groupByMonth) labels[producaoMesGroupColumnId(m.slug)] = mesLabel;
    for (const p of metricRefs) {
      const colKey = producaoPivotColumnKey(p.id, m.slug);
      const fieldLabel = metricLabels[p.id] || p.id;
      labels[colKey] = groupByMonth ? fieldLabel : `${mesLabel} \u2014 ${fieldLabel}`;
    }
  }
  return labels;
}

/** Labels para exportação plana (CSV/Excel): sempre inclui o mês no nome da coluna pivot. */
export function buildExportProducaoPivotColumnLabels(meta, columnRefs, appliedPeriod) {
  const labels = {};
  const metricRefs = getProducaoVolumetriaRefs(columnRefs);
  const metricLabels = Object.fromEntries(
    COLUMNS_CONFIG_PRODUCAO.map((c) => [c.id, c.label])
  );
  if (isProducaoAggregatedMode(columnRefs)) {
    for (const p of metricRefs) {
      const base = metricLabels[p.id] || p.id;
      const colKey = producaoTotalColumnKey(p.id);
      if (isProducaoMedColumnId(p.id)) {
        labels[colKey] = base;
      } else {
        labels[colKey] = isProducaoMediaMode(columnRefs) ? `${base} (m\u00e9dia)` : base;
      }
    }
    return labels;
  }
  let meses = resolveProducaoMeses(meta, columnRefs, appliedPeriod, []);
  if (meses.length === 0) {
    const def = getDefaultProducaoMesPeriodo();
    meses = buildMesesRangeFromPeriod(def.startDate, def.endDate);
  }
  for (const m of meses) {
    const mesLabel = m.label || mesLabelFromSlug(m.slug);
    for (const p of metricRefs) {
      const colKey = producaoPivotColumnKey(p.id, m.slug);
      const fieldLabel = metricLabels[p.id] || p.id;
      labels[colKey] = `${fieldLabel} (${mesLabel})`;
    }
  }
  return labels;
}
